import React, { useState, useEffect, useRef, useContext } from "react";
import { Html5QrcodeScanner, Html5QrcodeScanType } from "html5-qrcode";
import axios from "axios";
import {
  Snackbar,
  Alert,
  Container,
  Button,
  Grid,
  Paper,
  Typography,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  MenuItem,
  Box,
} from "@mui/material";
import { OrderContext } from "../contexts/OrderContext";
const sanitizeForDB = (value) => {
  if (typeof value !== "string") return value;
  return value.replace(/[^\x00-\xFF]/g, "");
};

const sanitizeObject = (obj) => {
  if (!obj || typeof obj !== "object") return obj;
  return Object.fromEntries(
    Object.entries(obj).map(([key, val]) => [key, sanitizeForDB(val)]),
  );
};

const QRScanner = () => {
  const { storeData } = useContext(OrderContext);
  const qrScannerRef = useRef(null);

  const [qrData, setQrData] = useState("");
  const [scanning, setScanning] = useState(false);
  const [apiResponse, setApiResponse] = useState(null);
  const [qcStatus, setQcStatus] = useState("1");
  const [error, setError] = useState(null);
  const [userRole, setUserRole] = useState("");
  const [rejectionMessage, setRejectionMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState(null);

  const karigarid =
    userRole === "Karigar" ? parseInt(localStorage.getItem("id"), 10) : null;

  const itemFieldLabels = {
    order_no: "Order No",
    store_order_no: "Order No",
    qr_code_no: "QR Code No",
    order_pcs: "Order Pieces",
    gross_with_label_wt: "Gross Weight with Label",
    gross_wt: "Gross Weight",
    net_wt: "Net Weight",
    total_stone_wt: "Total Stone Weight",
    sizelength: "Size Length",
  };

  const hiddenItemFields = [
    "id",
    "qr_code_no",
    "order_no",
    "store_order_no",
    "karigarid",
    "karigar_name",
    "createdby",
  ];
  const hiddenStoneFields = ["id"];

  // useEffect(() => {
  //   const role = localStorage.getItem("userRole") || "USER";
  //   setUserRole(role);

  //   if (scanning) {
  //     const scanner = new Html5QrcodeScanner("qr-reader", {
  //       qrbox: { width: 250, height: 250 },
  //       fps: 5,
  //     });
  //     scanner.render(onScanSuccess, onScanFailure);
  //     qrScannerRef.current = scanner;
  //   }

  //   return () => {
  //     if (qrScannerRef.current) qrScannerRef.current.clear();
  //   };
  // }, [scanning]);

  useEffect(() => {
    const role = localStorage.getItem("userRole") || "USER";
    setUserRole(role);

    if (scanning) {
      const config = {
        fps: 10, // Increased FPS for better detection
        qrbox: (viewfinderWidth, viewfinderHeight) => {
          // Dynamic QR box sizing based on viewport
          const minEdge = Math.min(viewfinderWidth, viewfinderHeight);
          const qrboxSize = Math.floor(minEdge * 0.7); // 70% of smallest dimension
          return {
            width: qrboxSize,
            height: qrboxSize,
          };
        },
        aspectRatio: 1.0, // Square aspect ratio
        // Mobile-friendly settings
        rememberLastUsedCamera: true,
        supportedScanTypes: [
          Html5QrcodeScanType.SCAN_TYPE_CAMERA, // Prefer camera over file upload
        ],
      };

      const scanner = new Html5QrcodeScanner("qr-reader", config);
      scanner.render(onScanSuccess, onScanFailure);
      qrScannerRef.current = scanner;
    }

    return () => {
      if (qrScannerRef.current) {
        qrScannerRef.current.clear().catch((err) => {
          console.warn("Error clearing scanner:", err);
        });
      }
    };
  }, [scanning]);
  const onScanSuccess = (decodedText) => {
    setQrData(decodedText);
    setScanning(false);
    fetchOrderDetails(decodedText);
  };

  const onScanFailure = (error) => {
    console.warn(`QR scan error: ${error}`);
  };

  const fetchOrderDetails = async (qrCode) => {
    if (!qrCode) {
      setError("Please enter or scan a QR code.");
      return;
    }
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/fetchorder/qr-details/${qrCode}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        },
      );
      setApiResponse(response.data);

      // Set initial QC status from the first item
      if (response.data[0]?.item?.qcflag !== null) {
        setQcStatus(response.data[0].item.qcflag.toString());
      } else {
        setQcStatus("1"); // Default to "QC Done"
      }
    } catch (err) {
      console.error(err);
      setError("Failed to fetch order details");
    }
  };

  const handleItemChange = (field, value) => {
    setApiResponse((prevAPIResponse) =>
      prevAPIResponse.map((res, index) =>
        index === 0 ? { ...res, item: { ...res.item, [field]: value } } : res,
      ),
    );
  };

  const handleStoneChange = (resIndex, stoneIndex, field, value) => {
    setApiResponse((prev) => {
      const updatedResponse = [...prev];
      const stones = [...updatedResponse[resIndex].stones];

      stones[stoneIndex] = {
        ...stones[stoneIndex],
        [field]: value,
        // Always store the karigarid in the stone object
        karigarid: Number(updatedResponse[resIndex].item.karigarid) || null,
      };

      if (field === "stone_pcs" || field === "per_pc_wt") {
        const pcs = parseFloat(stones[stoneIndex].stone_pcs) || 0;
        const perPcWt = parseFloat(stones[stoneIndex].per_pc_wt) || 0;
        stones[stoneIndex].stone_wt = (pcs * perPcWt).toFixed(3);
      }

      updatedResponse[resIndex].stones = stones;
      return updatedResponse;
    });
  };

  const addNewStone = (resIndex) => {
    setApiResponse((prev) => {
      const updatedResponse = [...prev];

      const newStone = {
        stone_type: "",
        description: "",
        stone_pcs: "",
        per_pc_wt: "",
        stone_wt: "0.000",
        karigarid: Number(updatedResponse[resIndex].item.karigarid) || null,
      };

      updatedResponse[resIndex] = {
        ...updatedResponse[resIndex],
        stones: [...updatedResponse[resIndex].stones, newStone],
      };

      return updatedResponse;
    });
  };

  const saveChanges = async () => {
    const createdBy = parseInt(localStorage.getItem("id"), 10);

    const allStones = apiResponse.flatMap((res) => res.stones || []);

    const hasZeroPcs = allStones.some(
      (stone) => parseFloat(stone.stone_pcs || 0) === 0,
    );

    if (hasZeroPcs) {
      setError("Stone Pcs cannot be 0. Please check your entries.");
      return;
    }

    const cleanedStones = allStones.filter(
      (stone) => stone.stone_type?.trim() && parseFloat(stone.stone_pcs) > 0,
    );

    const seen = new Set();
    const uniqueStones = [];
    for (const stone of cleanedStones) {
      const key = `${stone.stone_type}-${stone.description}-${stone.stone_pcs}-${stone.per_pc_wt}-${stone.karigarid ?? "null"}`;
      if (!seen.has(key)) {
        seen.add(key);

        // If karigarid is undefined, nullify it explicitly
        uniqueStones.push({
          ...stone,
          karigarid: stone.karigarid ?? null,
        });
      }
    }

    const payload = {
      item: {
        ...apiResponse[0].item,
        qcflag: parseInt(qcStatus, 10),
        createdBy: createdBy,
        karigarid: null, // Always set as needed
      },
      stones: uniqueStones,
    };
    // const payload = {
    //   item: sanitizeObject({
    //     ...apiResponse[0].item,
    //     qcflag: parseInt(qcStatus, 10),
    //     createdBy: createdBy,
    //     karigarid: null,
    //   }),
    //   stones: uniqueStones.map(sanitizeObject),
    // };

    try {
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/fetchorder/updateqrdetails`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        },
      );

      await fetchOrderDetails(qrData);
      setSuccessMessage("Order details updated successfully!");
    } catch (err) {
      setError("Failed to update order details");
    }
  };

  const saveRejectionMessage = async () => {
    if (!rejectionMessage) {
      setError("Please enter a rejection message.");
      return;
    }
    const payload = {
      orderid: parseInt(apiResponse?.order?.eid, 10),
      work_status_id: 8,
      reject_reason: rejectionMessage,
      qcflag: 0,
      qr_code_no: apiResponse?.item?.qr_code_no,
    };
    // const payload = {
    //   orderid: parseInt(apiResponse?.order?.eid, 10),
    //   work_status_id: 8,
    //   reject_reason: sanitizeForDB(rejectionMessage),
    //   qcflag: 0,
    //   qr_code_no: apiResponse?.item?.qr_code_no,
    // };
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/orders/updateQcStatus`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        },
      );

      // Refresh the data after successful update
      await fetchOrderDetails(qrData);
      setSuccessMessage("Rejection message saved successfully!");
    } catch (err) {
      setError("Failed to save rejection message");
    }
  };

  const calculateTotalStoneWeight = () => {
    let totalSum = 0;
    apiResponse?.map((res) =>
      res?.stones?.map(
        (stone) => (totalSum += parseFloat(stone.stone_wt || 0)),
      ),
    );
    return totalSum.toFixed(3) || "0.000";
  };

  return (
    <Container>
      <Grid container spacing={2} alignItems="center" justifyContent="center">
        <Grid item xs={12} md={4} style={{ textAlign: "center" }}>
          <div
            id="qr-reader"
            // style={{ width: "100%", maxWidth: "300px", margin: "0 auto" }}
            style={{
              width: "100%",
              maxWidth: "500px", // Increased from 300px
              margin: "0 auto",
              border: "2px solid #ddd",
              borderRadius: "8px",
              overflow: "hidden",
            }}
          ></div>
          <Button variant="contained" onClick={() => setScanning(true)}>
            Start Scan
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => setScanning(false)}
            style={{ marginLeft: "10px" }}
          >
            Stop Scan
          </Button>
        </Grid>

        <Grid
          item
          xs={12}
          md={1}
          style={{
            textAlign: "center",
            fontSize: "1.2rem",
            fontWeight: "bold",
          }}
        >
          OR
        </Grid>

        <Grid item xs={12} md={4} mt={2}>
          <TextField
            fullWidth
            label="Enter QR Code"
            variant="outlined"
            value={qrData}
            onChange={(e) => setQrData(e.target.value)}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={() => fetchOrderDetails(qrData)}
            style={{ marginTop: "10px", width: "100%" }}
          >
            Fetch Order
          </Button>
        </Grid>
      </Grid>

      {apiResponse && (
        <Paper style={{ padding: "20px", marginTop: "20px" }}>
          {/* Always show QC status selector */}
          <FormControl component="fieldset">
            <FormLabel>QC Status</FormLabel>
            <TextField
              select
              value={qcStatus}
              onChange={(e) => setQcStatus(e.target.value)}
              fullWidth
            >
              <MenuItem value="0">QC Reject</MenuItem>
              <MenuItem value="1">QC Done</MenuItem>
              <MenuItem value="2">To Polish</MenuItem>
              <MenuItem value="3">To Hall Marking</MenuItem>
            </TextField>
          </FormControl>

          {/* Show current status and updated by info */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt={2}
          >
            {/* {userRole === "MFR" && apiResponse?.length > 0 && apiResponse?.map((res) => (
              <>
                <Box>
                  <Typography variant="subtitle1">Current Status:</Typography>
                  {res?.item?.qcflag === 1 && (
                    <Typography style={{ color: "green", fontWeight: "bold" }}>
                      Accepted
                    </Typography>
                  )}
                  {res?.item?.qcflag === 0 && (
                    <Typography style={{ color: "red", fontWeight: "bold" }}>
                      Rejected
                    </Typography>
                  )}
                  {res?.item?.qcflag === 2 && (
                    <Typography style={{ color: "orange", fontWeight: "bold" }}>
                      To Polish
                    </Typography>
                  )}
                  {res?.item?.qcflag === 3 && (
                    <Typography style={{ color: "blue", fontWeight: "bold" }}>
                      To Hall Marking
                    </Typography>
                  )}
                </Box>

                <Box textAlign="right">
                  <Typography variant="body2" color="textSecondary">
                    Last Updated By
                  </Typography>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {res?.item?.createdby_name || "N/A"}
                  </Typography>
                </Box>
              </>
            ))} */}
            {userRole === "MFR" && apiResponse?.length > 0 && (
              <>
                <Box>
                  <Typography variant="subtitle1">Current Status:</Typography>

                  {apiResponse[0]?.item?.qcflag === 1 && (
                    <Typography style={{ color: "green", fontWeight: "bold" }}>
                      Accepted
                    </Typography>
                  )}
                  {apiResponse[0]?.item?.qcflag === 0 && (
                    <Typography style={{ color: "red", fontWeight: "bold" }}>
                      Rejected
                    </Typography>
                  )}
                  {apiResponse[0]?.item?.qcflag === 2 && (
                    <Typography style={{ color: "orange", fontWeight: "bold" }}>
                      To Polish
                    </Typography>
                  )}
                  {apiResponse[0]?.item?.qcflag === 3 && (
                    <Typography style={{ color: "blue", fontWeight: "bold" }}>
                      To Hall Marking
                    </Typography>
                  )}
                </Box>

                <Box textAlign="right">
                  <Typography variant="body2" color="textSecondary">
                    Last Updated By
                  </Typography>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {apiResponse[0]?.item?.createdby_name || "N/A"}
                  </Typography>
                </Box>
              </>
            )}
          </Box>

          {/* Rejection message field when QC Reject is selected */}
          {qcStatus === "0" && (
            <>
              <TextField
                fullWidth
                label="Rejection Message"
                variant="outlined"
                value={rejectionMessage}
                onChange={(e) => setRejectionMessage(e.target.value)}
                margin="normal"
                required
              />
              <Button
                variant="contained"
                color="error"
                onClick={saveRejectionMessage}
                fullWidth
                sx={{ mt: 2 }}
              >
                Save Rejection
              </Button>
            </>
          )}

          {/* Save button for other statuses */}
          {(userRole === "MFR" ||
            userRole === "COMFR" ||
            userRole === "Karigar") && (
            <Button
              variant="contained"
              color="primary"
              onClick={saveChanges}
              fullWidth
              sx={{ mt: 2 }}
            >
              Update Details
            </Button>
          )}
        </Paper>
      )}

      {apiResponse && (
        <Grid
          container
          spacing={2}
          style={{ marginTop: "20px", marginBottom: "100px" }}
        >
          {/* Order Details Section */}
          <Grid item xs={12} md={4}>
            <Paper style={{ padding: "20px", textAlign: "center" }}>
              {apiResponse?.length > 0 && (
                <>
                  <div style={{ overflowX: "auto", whiteSpace: "nowrap" }}>
                    {apiResponse[0].order.image_url ? (
                      JSON.parse(apiResponse[0].order.image_url).map(
                        (image, index) => (
                          <img
                            key={index}
                            src={image}
                            alt={`Order ${index}`}
                            style={{
                              width: "100%",
                              maxHeight: "200px",
                              objectFit: "cover",
                              marginBottom: "10px",
                            }}
                          />
                        ),
                      )
                    ) : (
                      <Typography>No Images Available</Typography>
                    )}
                  </div>
                  <Typography variant="h6" gutterBottom>
                    Order Details
                  </Typography>
                  <Typography>
                    Order ID: {apiResponse[0].order.storeorderno}
                  </Typography>
                  {userRole !== "Karigar" && (
                    <Typography>
                      Store: {apiResponse[0].order.store_name}
                    </Typography>
                  )}
                  <Typography>
                    Delivery Date: {apiResponse[0].order.deliverydate}
                  </Typography>
                  <Typography>
                    Karigar Delivery:{" "}
                    {apiResponse[0].order.karigar_delivery_date}
                  </Typography>
                  <Typography>
                    {apiResponse[0].order.itemtype_name} /{" "}
                    {apiResponse[0].order.design_name}
                  </Typography>
                  <Typography>
                    PCS: {apiResponse[0].order.pcs} | G:{" "}
                    {apiResponse[0].order.weight} | N:{" "}
                    {apiResponse[0].order.netwt}
                  </Typography>
                  <Typography>Size: {apiResponse[0].order.size_len}</Typography>
                  <Typography>
                    {apiResponse[0].order.screw_type_name} /{" "}
                    {apiResponse[0].order.finish_name}
                  </Typography>
                  <Typography>
                    Description: {apiResponse[0].order.description}
                  </Typography>
                  <Typography>
                    Weight Range: {apiResponse[0].order.wt_range}
                  </Typography>
                  {userRole !== "Karigar" && (
                    <Typography>
                      Customer Order No:{" "}
                      {apiResponse[0].order.customer_order_number}
                    </Typography>
                  )}
                </>
              )}
            </Paper>
          </Grid>

          {/* Editable Item Details */}
          <Grid item xs={12} md={8}>
            <Paper style={{ padding: "20px" }}>
              <Typography variant="h6" gutterBottom>
                Item Details
              </Typography>
              <Grid container spacing={2}>
                {apiResponse?.length > 0 &&
                  Object.keys(apiResponse[0].item)
                    .filter((key) => !hiddenItemFields.includes(key))
                    .map((key, index) => (
                      <Grid item xs={6} key={index}>
                        <TextField
                          fullWidth
                          label={itemFieldLabels[key] || key}
                          value={apiResponse[0].item[key]}
                          variant="outlined"
                          margin="dense"
                          disabled={userRole !== "MFR" && userRole !== "COMFR"}
                          onChange={(e) =>
                            handleItemChange(key, e.target.value)
                          }
                        />
                      </Grid>
                    ))}
              </Grid>
            </Paper>
          </Grid>

          {/* Stone Details Section */}
          <Grid item xs={12}>
            <Paper style={{ padding: "20px", marginTop: "20px" }}>
              <Typography variant="h6" gutterBottom>
                Stone Details
              </Typography>

              {apiResponse?.length > 0 &&
                apiResponse.map((res, resIndex) => (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      flexDirection: "column",
                      gap: 2,

                      width: "100%",
                      mb: 3,
                      border: "1px solid rgba(0, 10, 124, 0.27)",
                      borderRadius: 5,
                      px: 2,
                      py: 1.5,
                    }}
                  >
                    {/* Karigar Name */}
                    <Typography sx={{ fontSize: 16 }}>
                      Karigar:{" "}
                      <span style={{ fontSize: 16, fontWeight: 600 }}>
                        {res.stones[0]?.karigar_name || "--"}
                      </span>
                    </Typography>

                    {/* Edited Stone Details */}
                    {res.stones.map((stone, index) => (
                      <Grid container spacing={2} key={`${resIndex}-${index}`}>
                        {/* Stone Type */}
                        <Grid item xs={12} md={3}>
                          <TextField
                            select
                            fullWidth
                            label="Stone Type"
                            value={stone.stone_type}
                            onChange={(e) =>
                              handleStoneChange(
                                resIndex,
                                index,
                                "stone_type",
                                e.target.value,
                              )
                            }
                            variant="outlined"
                            margin="dense"
                            disabled={
                              userRole === "Karigar" &&
                              Number(res.item.karigarid) !== Number(karigarid)
                            }
                          >
                            {storeData?.stone?.map((stoneOption) => (
                              <MenuItem
                                key={stoneOption.id}
                                value={stoneOption.Stone_name}
                              >
                                {stoneOption.Stone_name}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>

                        {/* Description */}
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            label="Description"
                            value={stone.description}
                            onChange={(e) =>
                              handleStoneChange(
                                resIndex,
                                index,
                                "description",
                                e.target.value,
                              )
                            }
                            variant="outlined"
                            margin="dense"
                            disabled={
                              userRole === "Karigar" &&
                              Number(res.item.karigarid) !== Number(karigarid)
                            }
                          />
                        </Grid>

                        {/* Stone PCs */}
                        <Grid item xs={6} md={2}>
                          <TextField
                            fullWidth
                            label="Stone PCs"
                            value={stone.stone_pcs}
                            onChange={(e) =>
                              handleStoneChange(
                                resIndex,
                                index,
                                "stone_pcs",
                                e.target.value,
                              )
                            }
                            variant="outlined"
                            margin="dense"
                            type="number"
                            disabled={
                              userRole === "Karigar" &&
                              Number(res.item.karigarid) !== Number(karigarid)
                            }
                          />
                        </Grid>

                        {/* Per PC WT */}
                        <Grid item xs={6} md={2}>
                          <TextField
                            fullWidth
                            label="Per PC WT"
                            value={stone.per_pc_wt}
                            onChange={(e) =>
                              handleStoneChange(
                                resIndex,
                                index,
                                "per_pc_wt",
                                e.target.value,
                              )
                            }
                            variant="outlined"
                            margin="dense"
                            type="number"
                            disabled={
                              userRole === "Karigar" &&
                              Number(res.item.karigarid) !== Number(karigarid)
                            }
                          />
                        </Grid>

                        {/* Total WT */}
                        <Grid item xs={12} md={2}>
                          <TextField
                            fullWidth
                            label="Total WT"
                            value={stone.stone_wt}
                            variant="outlined"
                            margin="dense"
                            disabled
                          />
                        </Grid>
                      </Grid>
                    ))}

                    {/* Add Stone Button */}
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => addNewStone(resIndex)}
                      style={{ marginTop: "10px" }}
                      disabled={
                        userRole === "Karigar" &&
                        Number(res.item.karigarid) !== Number(karigarid)
                      }
                    >
                      + Add Stone
                    </Button>
                  </Box>
                ))}

              {/* Total Stone Weight */}
              <Typography variant="h6" style={{ marginTop: "20px" }}>
                Total Stone Weight: {calculateTotalStoneWeight()}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage(null)}
      >
        <Alert severity="success">{successMessage}</Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={3000}
        onClose={() => setError(null)}
      >
        <Alert severity="error">{error}</Alert>
      </Snackbar>
    </Container>
  );
};

export default QRScanner;
